import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';

/** A breadcrumb hop under the console base. Omit `slug` to mark the current page. */
export interface S3Crumb {
  label: ReactNode;
  /** Path segment under `/services/oss/:serviceId`; omit for the current (non-link) crumb. */
  slug?: string;
}

const ROOT_LABEL = 'Object Storage';

/**
 * Consistent breadcrumb trail for every service-scoped Object Storage page,
 * always rooted at the console index ("Object Storage"). Sub-pages pass their
 * hops via `trail`; the overview (index) passes none so the root reads current.
 */
export function S3ConsoleBreadcrumbs({ trail = [] }: { trail?: S3Crumb[] }) {
  const { serviceId } = useParams<{ serviceId: string }>();
  const base = `/services/oss/${serviceId}`;

  const items = [
    trail.length === 0 ? (
      <BreadcrumbItem key="root" current>{ROOT_LABEL}</BreadcrumbItem>
    ) : (
      <BreadcrumbItem key="root" asChild>
        <Link to={base}>{ROOT_LABEL}</Link>
      </BreadcrumbItem>
    ),
    ...trail.map((crumb, i) =>
      crumb.slug ? (
        <BreadcrumbItem key={i} asChild>
          <Link to={`${base}/${crumb.slug}`}>{crumb.label}</Link>
        </BreadcrumbItem>
      ) : (
        <BreadcrumbItem key={i} current>
          {crumb.label}
        </BreadcrumbItem>
      ),
    ),
  ];

  return <Breadcrumbs>{items}</Breadcrumbs>;
}
